import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, X, Trash2, Eye, Edit, BookOpen, Search, Check,
    CalendarDays, ChevronRight, BarChart2, Users, Layers,
    BadgeCheck, MailCheck, RefreshCw, Filter, ChevronDown, ChevronUp,
} from "lucide-react";

import {
    createCampagneRequest,
    deleteCampagneRequest,
    fetchCampagnesRequest,
    updateCampagneRequest,
} from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { getAllEntitesRequest } from "../../../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../../../redux/actions/userActions";
import { getAllJobsRequest } from "../../../../../redux/actions/Jobactions";
import MultiSelectDropdown from "../../../../../Ui/UiJs/MultiSelectDropdown";
import "../../RessourcesStyles/CampagneManagement.css"; // âœ… style Campagne dÃ©diÃ© (Attestation-like)

/* â”€â”€â”€ helpers â”€â”€â”€ */
const toDateInputValue = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().split("T")[0];
};
const uniqNums = (arr) =>
    Array.from(new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x))));
const splitAxes = (s) =>
    s ? String(s).split("\n").map((x) => x.trim()).filter(Boolean) : [];
const formatDateFR = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("fr-FR");
};

const EMPTY_FORM = {
    nom: "", reference: "", description: "",
    dateDebut: "", dateFin: "",
    anneeCampagne: new Date().getFullYear(),
    nombreMaxFormations: 1,
    niveauValidation: "MANAGER",
    envoiEmails: "INSTANTANE", dateEnvoiEmails: "",
    planificationRelance: "false",
    dateRelance: "", dateRelance2: "", dateRelance3: "",
    status: true,
};

/* â”€â”€â”€ PillRadio helper â”€â”€â”€ */
function PillRadioGroup({ name, options, value, onChange }) {
    return (
        <div className="cr-radio-group">
            {options.map(opt => (
                <label key={opt.value} className="cr-pill-radio">
                    <input
                        type="radio"
                        name={name}
                        value={opt.value}
                        checked={String(value) === String(opt.value)}
                        onChange={() => onChange(opt.value)}
                    />
                    <span className={`cr-pill${String(value) === String(opt.value) ? " active" : ""}`}>
                        {opt.label}
                    </span>
                </label>
            ))}
        </div>
    );
}

/* â”€â”€â”€ ViewModal â”€â”€â”€ */
function ViewModal({ open, campagne, onClose, catalogues, entites }) {
    if (!open || !campagne) return null;
    const getCatName = (id) => (catalogues || []).find(c => +c.id === +id)?.nom || `#${id}`;
    const getEntName = (id) => (entites || []).find(e => +e.id === +id)?.name || `#${id}`;
    const formatAxes = (a) => a ? String(a).split("\n").filter(Boolean).join(", ") : "—";

    return (
        <div className="cr-overlay" onClick={onClose}>
            <div className="cr-modal" onClick={e => e.stopPropagation()} role="dialog">
                <div className="cr-modal__header">
                    <div className="cr-modal__title">
                        <Eye size={16} style={{ color: "#ee645f" }} />
                        Détails — {campagne.nom}
                    </div>
                    <button className="cr-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>
                <div className="cr-modal__body">
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Informations générales</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Nom</span><span className="cr-view-value cr-strong">{campagne.nom}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Référence</span><span className="cr-view-value cr-muted">{campagne.reference || "—"}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Année</span><span className="cr-badge cr-badge--blue">{campagne.anneeCampagne}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Statut</span>{campagne.status ? <span className="cr-badge cr-badge--green">Active</span> : <span className="cr-badge cr-badge--grey">Inactive</span>}</div>
                            <div className="cr-view-item"><span className="cr-view-label">Max de formations</span><span className="cr-badge cr-badge--blue">{campagne.nombreMaxFormations || 0}</span></div>
                            {campagne.description && <div className="cr-view-item cr-view-item--full"><span className="cr-view-label">Description</span><span className="cr-view-value cr-muted">{campagne.description}</span></div>}
                            {campagne.axes && <div className="cr-view-item cr-view-item--full"><span className="cr-view-label">Axes</span><span className="cr-view-value cr-muted">{formatAxes(campagne.axes)}</span></div>}
                        </div>
                    </div>
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Dates</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Début</span><span className="cr-view-value">{formatDateFR(campagne.dateDebut)}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Fin</span><span className="cr-view-value">{formatDateFR(campagne.dateFin)}</span></div>
                        </div>
                    </div>
                    <div className="cr-view-section">
                        <h3 className="cr-view-title">Configuration</h3>
                        <div className="cr-view-grid">
                            <div className="cr-view-item"><span className="cr-view-label">Validation</span><span className="cr-badge cr-badge--blue">{campagne.validation || "—"}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Emails</span><span className="cr-badge cr-badge--blue">{campagne.envoiEmails || "—"}</span></div>
                            <div className="cr-view-item"><span className="cr-view-label">Filter mode</span><span className="cr-badge cr-badge--grey">{campagne.filterMode || "—"}</span></div>
                        </div>
                    </div>
                    {campagne.budgets?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Budgets</h3>
                            {campagne.budgets.map((b, i) => (
                                <div key={i} className="cr-budget-row">
                                    <div>
                                        <div className="cr-strong" style={{ fontSize: 12 }}>{b.typeBudget}</div>
                                        {b.entiteId && <div className="cr-muted" style={{ fontSize: 11 }}>{getEntName(b.entiteId)}</div>}
                                    </div>
                                    <span className="cr-budget-amount">{(b.montant || 0).toLocaleString("fr-FR")} {b.currency}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {campagne.catalogueIds?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Catalogues</h3>
                            <div className="cr-tag-list">
                                {campagne.catalogueIds.map(id => <span key={id} className="cr-badge cr-badge--blue">{getCatName(id)}</span>)}
                            </div>
                        </div>
                    )}
                    {campagne.entiteIds?.length > 0 && (
                        <div className="cr-view-section">
                            <h3 className="cr-view-title">Entités ciblées</h3>
                            <div className="cr-tag-list">
                                {campagne.entiteIds.map(id => <span key={id} className="cr-badge cr-badge--grey">{getEntName(id)}</span>)}
                            </div>
                        </div>
                    )}
                </div>
                <div className="cr-modal__footer">
                    <button className="cr-btn cr-btn--ghost" type="button" onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
}

/* â”€â”€â”€ Inline Users Panel â”€â”€â”€ */
function InlineUsersPanel({ users, usersState, setUsersState, setSelectedUsers, entiteIds, posteIds }) {
    const [search, setSearch] = useState("");
    const [autoFilter, setAutoFilter] = useState(false);

    const AVATAR_COLORS = ["#204385", "#ee645f", "#0891b2", "#059669", "#7c3aed", "#d97706"];
    const getAvatarColor = (id) => AVATAR_COLORS[+id % AVATAR_COLORS.length];

    const getInitials = (u) => {
        const first = (u.firstName || u.prenom || "?")[0];
        const last = (u.lastName || u.nom || "")[0] || "";
        return `${first}${last}`.toUpperCase();
    };
    const getFullName = (u) =>
        `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() || `User #${u.id}`;

    const baseList = useMemo(() => {
        if (!autoFilter || (!entiteIds.length && !posteIds.length)) return usersState || [];
        return (usersState || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [usersState, autoFilter, entiteIds, posteIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseList;
        return baseList.filter(u =>
            `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""} ${u.email || ""}`.toLowerCase().includes(q)
        );
    }, [baseList, search]);

    const toggleUser = (userId) => {
        const updated = usersState.map(u =>
            +u.id === +userId ? { ...u, checked: !u.checked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const toggleAll = () => {
        const allChecked = filtered.every(u => u.checked);
        const filteredIds = new Set(filtered.map(u => +u.id));
        const updated = usersState.map(u =>
            filteredIds.has(+u.id) ? { ...u, checked: !allChecked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const clearAll = () => {
        const updated = usersState.map(u => ({ ...u, checked: false }));
        setUsersState(updated);
        setSelectedUsers([]);
    };

    const checkedUsers = (usersState || []).filter(u => u.checked);
    const allFilteredChecked = filtered.length > 0 && filtered.every(u => u.checked);
    const hasAutoFilterOption = entiteIds.length > 0 || posteIds.length > 0;

    return (
        <div className="cr-users-panel">
            <div className="cr-users-panel__header">
                <div className="cr-users-panel__title">
                    <Users size={14} />
                    Collaborateurs
                </div>
                <div className="cr-users-header-right">
                    {hasAutoFilterOption && (
                        <button
                            type="button"
                            className={`cr-auto-filter-btn${autoFilter ? " active" : ""}`}
                            onClick={() => setAutoFilter(p => !p)}
                            title="Filtrer selon les entités et postes sélectionnés"
                        >
                            <Filter size={12} />
                            Filtrer par entités/postes
                        </button>
                    )}
                    <div className="cr-users-search">
                        <Search size={12} className="cr-users-search-icon" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="cr-users-search-input"
                        />
                    </div>
                </div>
            </div>

            {checkedUsers.length > 0 && (
                <div className="cr-users-summary">
                    <div className="cr-avatar-stack">
                        {checkedUsers.slice(0, 5).map(u => (
                            <div
                                key={u.id}
                                className="cr-avatar-stack__item"
                                style={{ background: getAvatarColor(u.id) }}
                                title={getFullName(u)}
                            >
                                {getInitials(u)}
                            </div>
                        ))}
                        {checkedUsers.length > 5 && (
                            <div className="cr-avatar-stack__more">+{checkedUsers.length - 5}</div>
                        )}
                    </div>
                    <span className="cr-users-summary__label">
                        <strong>{checkedUsers.length}</strong> collaborateur{checkedUsers.length > 1 ? "s" : ""} sélectionné{checkedUsers.length > 1 ? "s" : ""}
                    </span>
                    <button type="button" className="cr-users-summary__clear" onClick={clearAll}>
                        <X size={11} /> Tout désélectionner
                    </button>
                </div>
            )}

            {(usersState || []).length === 0 ? (
                <div className="cr-users-empty">
                    <Users size={28} style={{ color: "#d1d5db", marginBottom: 8 }} />
                    <div>Aucun utilisateur disponible</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                        Vérifiez que les utilisateurs sont chargés depuis le store Redux
                    </div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="cr-users-empty">
                    Aucun résultat{search ? ` pour « ${search} »` : " avec ce filtre"}
                </div>
            ) : (
                <>
                    <div className="cr-users-select-all" onClick={toggleAll}>
                        <div className={`cr-user-checkbox${allFilteredChecked ? " checked" : ""}`}>
                            {allFilteredChecked && <Check size={10} />}
                        </div>
                        <span className="cr-users-select-text">
                            {allFilteredChecked ? "Tout désélectionner" : "Tout sélectionner"} ({filtered.length})
                        </span>
                    </div>
                    <div className="cr-users-list">
                        {filtered.map(u => (
                            <div
                                key={u.id}
                                className={`cr-user-row${u.checked ? " checked" : ""}`}
                                onClick={() => toggleUser(u.id)}
                            >
                                <div className={`cr-user-checkbox${u.checked ? " checked" : ""}`}>
                                    {u.checked && <Check size={10} />}
                                </div>
                                <div
                                    className="cr-user-avatar"
                                    style={u.checked ? { background: getAvatarColor(u.id), color: "#fff" } : {}}
                                >
                                    {getInitials(u)}
                                </div>
                                <div className="cr-user-info">
                                    <div className="cr-user-name">{getFullName(u)}</div>
                                    {u.email && <div className="cr-user-email">{u.email}</div>}
                                </div>
                                {u.poste && (
                                    <span className="cr-badge cr-badge--grey" style={{ fontSize: 10, marginLeft: "auto" }}>
                                        {u.poste}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/* â”€â”€â”€ FormModal â”€â”€â”€ */
function FormModal({ open, campagneId, campagnes, onClose, onSave, isLoading, catalogues, entites, jobs, users }) {
    const campagne = useMemo(() =>
            campagneId ? (campagnes || []).find(c => +c.id === +campagneId) || null : null,
        [campagneId, campagnes]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [filterMode, setFilterMode] = useState("IN");
    const [managerLevelFilter, setManagerLevelFilter] = useState("");
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialUserIds, setInitialUserIds] = useState([]);
    const [catalogueIds, setCatalogueIds] = useState([]);
    const [axeToAdd, setAxeToAdd] = useState("");
    const [axesArray, setAxesArray] = useState([]);
    const [typeBudget, setTypeBudget] = useState("Global");
    const [currency, setCurrency] = useState("MAD");
    const [budgetGlobal, setBudgetGlobal] = useState("");
    const [budgetsByEntite, setBudgetsByEntite] = useState([]);
    const [errors, setErrors] = useState({});
    const [showUsersPanel, setShowUsersPanel] = useState(false);
    const [activeSection, setActiveSection] = useState("identification");

    // --- New Activation Fields ---
    const [activationPurpose, setActivationPurpose] = useState("");
    const [activationUserIds, setActivationUserIds] = useState([]);
    const [activationDateDebut, setActivationDateDebut] = useState("");
    const [activationDateFin, setActivationDateFin] = useState("");
    const [isDefault, setIsDefault] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!campagne) {
            setForm(EMPTY_FORM);
            setFilterMode("IN"); setManagerLevelFilter(""); setEntiteIds([]); setPosteIds([]);
            setCatalogueIds([]); setTypeBudget("Global"); setCurrency("MAD");
            setBudgetGlobal(""); setBudgetsByEntite([]);
            setUsersState([]); setSelectedUsers([]); setInitialUserIds([]);
            setAxeToAdd(""); setAxesArray([]); setErrors({}); setActiveSection("identification");
            setShowUsersPanel(false);
            setActivationPurpose(""); setActivationUserIds([]);
            setActivationDateDebut(""); setActivationDateFin(""); setIsDefault(false);
            return;
        }
        setForm({
            nom: campagne.nom || "", reference: campagne.reference || "",
            description: campagne.description || "",
            dateDebut: toDateInputValue(campagne.dateDebut),
            dateFin: toDateInputValue(campagne.dateFin),
            anneeCampagne: campagne.anneeCampagne || new Date().getFullYear(),
            nombreMaxFormations: campagne.nombreMaxFormations || 1,
            niveauValidation: campagne.validation || campagne.niveauValidation || "MANAGER",
            envoiEmails: campagne.envoiEmails || "INSTANTANE",
            dateEnvoiEmails: toDateInputValue(campagne.dateEnvoiEmails),
            planificationRelance: String(campagne.planificationRelance ?? "false"),
            dateRelance: toDateInputValue(campagne.dateRelance),
            dateRelance2: toDateInputValue(campagne.dateRelance2),
            dateRelance3: toDateInputValue(campagne.dateRelance3),
            status: Boolean(campagne.status ?? true),
        });
        setFilterMode(campagne.filterMode || "IN");
        setManagerLevelFilter(campagne.managerLevelFilter || "");
        setEntiteIds(uniqNums(campagne.entiteIds || []));
        setPosteIds(uniqNums(campagne.posteIds || []));
        setCatalogueIds(uniqNums(campagne.catalogueIds || []));
        setInitialUserIds(uniqNums(campagne.userIds || []));
        setAxesArray(splitAxes(campagne.axes));
        const bList = campagne.budgets || [];
        const b0 = bList[0];
        if (b0) {
            setTypeBudget(b0.typeBudget || "Global");
            setCurrency(b0.currency || "MAD");
            if ((b0.typeBudget || "") === "Global") {
                setBudgetGlobal(String(b0.montant ?? ""));
                setBudgetsByEntite([]);
            } else {
                setBudgetGlobal("");
                setBudgetsByEntite(bList.map(b => ({
                    entiteId: b.entiteId ?? "",
                    currency: b.currency || "MAD",
                    montant: b.montant != null ? String(b.montant) : ""
                })));
            }
        }
        setErrors({}); setActiveSection("identification"); setShowUsersPanel(false);

        setActivationPurpose(campagne.activationPurpose || "");
        setActivationUserIds(uniqNums(campagne.activationUserIds || []));
        setActivationDateDebut(toDateInputValue(campagne.activationDateDebut));
        setActivationDateFin(toDateInputValue(campagne.activationDateFin));
        setIsDefault(Boolean(campagne.isDefault || false));
    }, [open, campagne]);

    useEffect(() => {
        const prevMap = new Map((usersState || []).map(u => [+u.id, !!u.checked]));
        const initSet = new Set((initialUserIds || []).map(x => +x));
        const next = (users || []).map(u => ({
            ...u,
            checked: prevMap.has(+u.id) ? prevMap.get(+u.id) : initSet.has(+u.id)
        }));
        setUsersState(next);
        setSelectedUsers(next.filter(u => u.checked));
    }, [users, initialUserIds]);

    const filteredUsersForActivation = useMemo(() => {
        if (!entiteIds.length && !posteIds.length) return users || [];
        return (users || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [users, entiteIds, posteIds]);

    const toggleId = (arr, id) => {
        const n = +id;
        return arr.includes(n) ? arr.filter(x => x !== n) : [...arr, n];
    };

    const validate = () => {
        const e = {};
        if (!form.nom.trim()) e.nom = "Obligatoire";
        if (!form.description.trim()) e.description = "Obligatoire";
        if (!catalogueIds.length) e.catalogues = "Sélectionne au moins un catalogue";
        if (typeBudget === "Global") {
            if (!budgetGlobal || +budgetGlobal <= 0) e.budgetGlobal = "Budget global invalide";
        } else {
            const clean = budgetsByEntite.filter(r => r.entiteId || r.montant);
            if (!clean.length || clean.some(r => !r.entiteId || +r.montant <= 0)) e.budgetsByEntite = "Budget par entité invalide";
        }
        if (form.envoiEmails === "DIFFERE" && !form.dateEnvoiEmails) e.dateEnvoiEmails = "Date obligatoire";
        if (form.planificationRelance === "true" && !form.dateRelance) e.dateRelance = "Date relance 1 obligatoire";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const buildPayload = () => {
        const budgets = typeBudget === "Global"
            ? [{ montant: +budgetGlobal, currency, typeBudget: "Global" }]
            : budgetsByEntite.filter(r => r.entiteId && r.montant).map(r => ({
                entiteId: +r.entiteId,
                montant: +r.montant,
                currency: r.currency || currency,
                typeBudget: "Par entité"
            }));
        return {
            nom: form.nom, reference: form.reference, description: form.description,
            dateDebut: form.dateDebut || null, dateFin: form.dateFin || null,
            anneeCampagne: +form.anneeCampagne,
            typeCampagne: "FORMATION",
            filterMode, managerLevelFilter: managerLevelFilter || null,
            entiteIds: uniqNums(entiteIds), posteIds: uniqNums(posteIds),
            userIds: uniqNums(selectedUsers.map(u => u.id)),
            catalogueIds: uniqNums(catalogueIds),
            nombreMaxFormations: +form.nombreMaxFormations,
            recruitmentLimits: [],
            validation: form.niveauValidation,
            envoiEmails: form.envoiEmails,
            dateEnvoiEmails: form.envoiEmails === "DIFFERE" ? form.dateEnvoiEmails || null : null,
            planificationRelance: form.planificationRelance === "true",
            dateRelance: form.planificationRelance === "true" ? form.dateRelance || null : null,
            dateRelance2: form.planificationRelance === "true" ? form.dateRelance2 || null : null,
            dateRelance3: form.planificationRelance === "true" ? form.dateRelance3 || null : null,
            axes: axesArray.join("\n"),
            status: Boolean(form.status),
            budgets,
            activationPurpose,
            activationUserIds: uniqNums(activationUserIds),
            activationDateDebut: activationDateDebut || null,
            activationDateFin: activationDateFin || null,
            isDefault: Boolean(isDefault),
        };
    };

    const handleSubmit = () => { if (!validate()) return; onSave(campagneId, buildPayload()); };

    const addAxe = () => {
        const v = axeToAdd.trim();
        if (!v || axesArray.includes(v)) { setAxeToAdd(""); return; }
        setAxesArray(p => [...p, v]);
        setAxeToAdd("");
    };

    const sf = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

    const sections = [
        { id: "identification", label: "Identification" },
        { id: "catalogues", label: "Catalogues" },
        { id: "population", label: "Population" },
        { id: "activation", label: "Activation" },
        { id: "validation", label: "Validation" },
        { id: "budget", label: "Budget" },
        { id: "relances", label: "Relances" },
        { id: "axes", label: "Axes" },
        { id: "autres", label: "Autres" },
    ];

    const sectionHasError = (id) => {
        if (id === "identification") return !!(errors.nom || errors.description);
        if (id === "catalogues") return !!errors.catalogues;
        if (id === "budget") return !!(errors.budgetGlobal || errors.budgetsByEntite);
        if (id === "relances") return !!errors.dateRelance;
        if (id === "autres") return !!errors.dateEnvoiEmails;
        return false;
    };

    if (!open) return null;

    return (
        <div className="cr-overlay" style={{ zIndex: 9998 }}>
            <div className="cr-modal cr-modal--wide" role="dialog">
                <div className="cr-modal__header">
                    <div className="cr-modal__title">
                        <BookOpen size={16} style={{ color: "#ee645f" }} />
                        {campagneId ? "Modifier la campagne" : "Créer une campagne de formation"}
                    </div>
                    <button className="cr-icon-btn" onClick={onClose} type="button"><X size={16} /></button>
                </div>

                {/* Section nav */}
                <div className="cr-section-nav">
                    {sections.map(s => (
                        <button key={s.id} type="button"
                                className={`cr-section-tab${activeSection === s.id ? " active" : ""}${sectionHasError(s.id) ? " has-error" : ""}`}
                                onClick={() => setActiveSection(s.id)}>
                            {s.label}
                            {sectionHasError(s.id) && <span className="cr-tab-dot" />}
                        </button>
                    ))}
                </div>

                <div className="cr-modal__body cr-form-body">

                    {/* â”€â”€ IDENTIFICATION â”€â”€ */}
                    {activeSection === "identification" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Identification</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Nom *</label>
                                    <input value={form.nom} onChange={sf("nom")} placeholder="Ex: Campagne Formation 2025" />
                                    {errors.nom && <div className="cr-error">{errors.nom}</div>}
                                </div>
                                <div className="cr-field">
                                    <label>Référence</label>
                                    <input value={form.reference} onChange={sf("reference")} placeholder="Ex: CPG-FM-2025-001" />
                                </div>
                                <div className="cr-field cr-field--full">
                                    <label>Description *</label>
                                    <textarea value={form.description} onChange={sf("description")} rows={3} placeholder="Description de la campagne..." />
                                    {errors.description && <div className="cr-error">{errors.description}</div>}
                                </div>
                                <div className="cr-field">
                                    <label>Date début</label>
                                    <input type="date" value={form.dateDebut} onChange={sf("dateDebut")} className="cr-input-date" />
                                </div>
                                <div className="cr-field">
                                    <label>Date fin</label>
                                    <input type="date" value={form.dateFin} onChange={sf("dateFin")} className="cr-input-date" />
                                </div>
                                <div className="cr-field">
                                    <label>Année campagne</label>
                                    <input type="number" value={form.anneeCampagne} onChange={sf("anneeCampagne")} className="cr-input-short" />
                                </div>
                                <div className="cr-field">
                                    <label>Campagne par défaut</label>
                                    <div style={{ display: "flex", gap: 20, marginTop: 10 }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="isDefaultId"
                                                checked={isDefault === true}
                                                onChange={() => setIsDefault(true)}
                                            />
                                            <span>Oui</span>
                                        </label>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                            <input
                                                type="radio"
                                                name="isDefaultId"
                                                checked={isDefault === false}
                                                onChange={() => setIsDefault(false)}
                                            />
                                            <span>Non</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="cr-field">
                                    <label>Max de formations par collaborateur</label>
                                    <input type="number" min={1} value={form.nombreMaxFormations} onChange={sf("nombreMaxFormations")} className="cr-input-short" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* â”€â”€ CATALOGUES â”€â”€ */}
                    {activeSection === "catalogues" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Catalogues</h3>
                            {errors.catalogues && <div className="cr-error" style={{ marginBottom: 12 }}>{errors.catalogues}</div>}
                            <div className="cr-catalogue-grid">
                                {(catalogues || []).map(c => {
                                    const active = catalogueIds.includes(+c.id);
                                    return (
                                        <div key={c.id}
                                             className={`cr-catalogue-card${active ? " active" : ""}`}
                                             onClick={() => setCatalogueIds(p => toggleId(p, c.id))}>
                                            <div className="cr-catalogue-check">
                                                {active ? <Check size={13} /> : null}
                                            </div>
                                            <span className="cr-catalogue-name">{c.nom || c.name || `Catalogue #${c.id}`}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="cr-hint">{catalogueIds.length} catalogue(s) sélectionné(s)</div>
                        </div>
                    )}

                    {/* â”€â”€ POPULATION â”€â”€ */}
                    {activeSection === "population" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Population cible</h3>

                            <div className="cr-filters-inline">
                                {/* Mode de filtrage */}
                                <div className="cr-filter-group">
                                    <span className="cr-filter-group__label">Mode de filtrage</span>
                                    <div className="cr-filter-group__options">
                                        {[
                                            { value: "IN", label: "IN" },
                                            { value: "NOT_IN", label: "NOT IN" },
                                        ].map(opt => (
                                            <label key={opt.value} className="cr-pill-radio">
                                                <input
                                                    type="radio"
                                                    name="filterMode"
                                                    value={opt.value}
                                                    checked={filterMode === opt.value}
                                                    onChange={() => setFilterMode(opt.value)}
                                                />
                                                <span className={`cr-pill${filterMode === opt.value ? " active" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="cr-filters-divider" />

                                {/* Niveau de manager */}
                                <div className="cr-filter-group">
                                    <span className="cr-filter-group__label">Niveau manager</span>
                                    <div className="cr-filter-group__options">
                                        {[
                                            { value: "", label: "Aucun" },
                                            { value: "n+1", label: "N+1" },
                                            { value: "n+2", label: "N+2" },
                                        ].map(opt => (
                                            <label key={opt.value} className="cr-pill-radio">
                                                <input
                                                    type="radio"
                                                    name="managerLevel"
                                                    value={opt.value}
                                                    checked={managerLevelFilter === opt.value}
                                                    onChange={() => setManagerLevelFilter(opt.value)}
                                                />
                                                <span className={`cr-pill${managerLevelFilter === opt.value ? " active" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* EntitÃ©s & Postes */}
                            <div className="cr-form-grid" style={{ marginTop: 14 }}>
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Entités"
                                        items={entites || []}
                                        value={entiteIds}
                                        onChange={setEntiteIds}
                                        placeholder={entiteIds.length > 0 ? `${entiteIds.length} sélectionnée(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => x.nom || x.name || `#${x.id}`}
                                    />
                                    {entiteIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {entiteIds.map(id => {
                                                const ent = (entites || []).find(e => +e.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                        {ent ? (ent.nom || ent.name) : `#${id}`}
                                                        <button type="button" onClick={() => setEntiteIds(p => p.filter(x => x !== id))}>
                                                            <X size={9} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Postes"
                                        items={jobs || []}
                                        value={posteIds}
                                        onChange={setPosteIds}
                                        placeholder={posteIds.length > 0 ? `${posteIds.length} sélectionné(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => x.nom || x.name || `#${x.id}`}
                                    />
                                    {posteIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {posteIds.map(id => {
                                                const job = (jobs || []).find(j => +j.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                        {job ? (job.nom || job.name) : `#${id}`}
                                                        <button type="button" onClick={() => setPosteIds(p => p.filter(x => x !== id))}>
                                                            <X size={9} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Collaborateurs toggle */}
                            <div className="cr-collab-toggle" style={{ marginTop: 16 }}>
                                <div className="cr-collab-toggle__info">
                                    <div className="cr-strong" style={{ fontSize: 12 }}>Collaborateurs</div>
                                    <div className="cr-hint">
                                        {selectedUsers.length > 0
                                            ? `${selectedUsers.length} collaborateur${selectedUsers.length > 1 ? "s" : ""} sélectionné${selectedUsers.length > 1 ? "s" : ""}`
                                            : "Aucun collaborateur sélectionné"}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={`cr-btn${showUsersPanel ? " cr-btn--ghost" : " cr-btn--primary"}`}
                                    onClick={() => setShowUsersPanel(p => !p)}
                                >
                                    <Users size={14} />
                                    {showUsersPanel ? "Masquer" : "Afficher les collaborateurs"}
                                    {showUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                </button>
                            </div>

                            {showUsersPanel && (
                                <InlineUsersPanel
                                    users={users || []}
                                    usersState={usersState}
                                    setUsersState={setUsersState}
                                    setSelectedUsers={setSelectedUsers}
                                    filterMode={filterMode}
                                    managerLevelFilter={managerLevelFilter}
                                    entiteIds={entiteIds}
                                    posteIds={posteIds}
                                />
                            )}
                        </div>
                    )}

                    {/* â”€â”€ ACTIVATION â”€â”€ */}
                    {activeSection === "activation" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Conditions d'activation</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <MultiSelectDropdown
                                        label="Pour qui (Population active)"
                                        items={filteredUsersForActivation || []}
                                        value={activationUserIds}
                                        onChange={setActivationUserIds}
                                        placeholder={activationUserIds.length > 0 ? `${activationUserIds.length} sélectionné(s)` : "Sélectionner..."}
                                        getId={x => +x.id}
                                        getLabel={x => `${x.firstName || x.prenom || ""} ${x.lastName || x.nom || ""}`.trim() || `#${x.id}`}
                                    />
                                    {activationUserIds.length > 0 && (
                                        <div className="cr-selected-tags">
                                            {activationUserIds.map(id => {
                                                const u = (users || []).find(x => +x.id === id);
                                                return (
                                                    <span key={id} className="cr-selected-tag">
                                                        {u ? `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() : `#${id}`}
                                                        <button type="button" onClick={() => setActivationUserIds(p => p.filter(x => x !== id))}>
                                                            <X size={9} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="cr-field">
                                    <label>Pour quoi (Objectif)</label>
                                    <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                                        {[
                                            { value: "DEMANDER", label: "Demander" },
                                            { value: "VALIDER", label: "Valider" },
                                            { value: "TRAITER", label: "Traiter" },
                                        ].map(opt => (
                                            <label key={opt.value} className="cr-pill-radio">
                                                <input
                                                    type="radio"
                                                    name="activationPurpose"
                                                    value={opt.value}
                                                    checked={activationPurpose === opt.value}
                                                    onChange={() => setActivationPurpose(opt.value)}
                                                />
                                                <span className={`cr-pill${activationPurpose === opt.value ? " active" : ""}`}>
                                                    {opt.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="cr-field">
                                    <label>Date début activation</label>
                                    <input type="date" value={activationDateDebut} onChange={e => setActivationDateDebut(e.target.value)} />
                                </div>
                                <div className="cr-field">
                                    <label>Date fin activation</label>
                                    <input type="date" value={activationDateFin} onChange={e => setActivationDateFin(e.target.value)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* â”€â”€ VALIDATION â”€â”€ */}
                    {activeSection === "validation" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Niveau de validation</h3>
                            <div className="cr-field">
                                <label>Niveau de validation</label>
                                <PillRadioGroup
                                    name="niveauValidation"
                                    options={[
                                        { value: "MANAGER", label: "Manager" },
                                        { value: "RESPONSABLE_FORMATION", label: "Responsable de formation" },
                                        { value: "BOTH", label: "Les deux" },
                                    ]}
                                    value={form.niveauValidation}
                                    onChange={(val) => setForm(p => ({ ...p, niveauValidation: val }))}
                                />
                            </div>
                        </div>
                    )}

                    {/* â”€â”€ BUDGET â”€â”€ */}
                    {activeSection === "budget" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Budget</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Type budget</label>
                                    <PillRadioGroup
                                        name="typeBudget"
                                        options={[
                                            { value: "Global", label: "Global" },
                                            { value: "Par entité", label: "Par entité" },
                                        ]}
                                        value={typeBudget}
                                        onChange={setTypeBudget}
                                    />
                                </div>
                                <div className="cr-field">
                                    <label>Devise</label>
                                    <PillRadioGroup
                                        name="currency"
                                        options={[
                                            { value: "MAD", label: "MAD" },
                                            { value: "EUR", label: "EUR" },
                                            { value: "USD", label: "USD" },
                                        ]}
                                        value={currency}
                                        onChange={setCurrency}
                                    />
                                </div>
                            </div>
                            {typeBudget === "Global" && (
                                <div className="cr-field" style={{ marginTop: 12 }}>
                                    <label>Budget global *</label>
                                    <input type="number" min={0} value={budgetGlobal} onChange={e => setBudgetGlobal(e.target.value)} placeholder="Ex: 100000" className="cr-input-short" />
                                    {errors.budgetGlobal && <div className="cr-error">{errors.budgetGlobal}</div>}
                                </div>
                            )}
                            {typeBudget === "Par entité" && (
                                <div style={{ marginTop: 12 }}>
                                    {errors.budgetsByEntite && <div className="cr-error" style={{ marginBottom: 8 }}>{errors.budgetsByEntite}</div>}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                        <span className="cr-strong" style={{ fontSize: 12 }}>Budgets par entité</span>
                                        <button type="button" className="cr-btn cr-btn--primary" onClick={() => setBudgetsByEntite(p => [...p, { entiteId: "", currency, montant: "" }])}>+ Ajouter</button>
                                    </div>
                                    {budgetsByEntite.map((row, idx) => (
                                        <div key={idx} className="cr-form-grid" style={{ marginBottom: 10 }}>
                                            <div className="cr-field">
                                                <label>Entité *</label>
                                                <select value={row.entiteId} onChange={e => setBudgetsByEntite(p => p.map((r, i) => i === idx ? { ...r, entiteId: e.target.value } : r))}>
                                                    <option value="">—</option>
                                                    {(entites || []).map(en => <option key={en.id} value={en.id}>{en.nom || en.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="cr-field">
                                                <label>Montant *</label>
                                                <div style={{ display: "flex", gap: 8 }}>
                                                    <input type="number" min={0} value={row.montant} onChange={e => setBudgetsByEntite(p => p.map((r, i) => i === idx ? { ...r, montant: e.target.value } : r))} style={{ flex: 1 }} />
                                                    <button type="button" className="cr-btn cr-btn--danger" onClick={() => setBudgetsByEntite(p => p.filter((_, i) => i !== idx))}>×</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* â”€â”€ RELANCES â”€â”€ */}
                    {activeSection === "relances" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Relances planifiées</h3>
                            <div className="cr-field" style={{ marginBottom: 14 }}>
                                <label>Relances planifiées</label>
                                <PillRadioGroup
                                    name="planificationRelance"
                                    options={[
                                        { value: "false", label: "Non" },
                                        { value: "true", label: "Oui" },
                                    ]}
                                    value={form.planificationRelance}
                                    onChange={(val) => setForm(p => ({ ...p, planificationRelance: val }))}
                                />
                            </div>
                            {form.planificationRelance === "true" && (
                                <div className="cr-form-grid">
                                    <div className="cr-field">
                                        <label>Date relance 1 *</label>
                                        <input type="date" value={form.dateRelance} onChange={sf("dateRelance")} className="cr-input-date" />
                                        {errors.dateRelance && <div className="cr-error">{errors.dateRelance}</div>}
                                    </div>
                                    <div className="cr-field">
                                        <label>Date relance 2</label>
                                        <input type="date" value={form.dateRelance2} onChange={sf("dateRelance2")} className="cr-input-date" />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date relance 3</label>
                                        <input type="date" value={form.dateRelance3} onChange={sf("dateRelance3")} className="cr-input-date" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* â”€â”€ AXES â”€â”€ */}
                    {activeSection === "axes" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Axes de formation</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field cr-field--full">
                                    <label>Ajouter un axe</label>
                                    <div style={{ display: "flex", gap: 10 }}>
                                        <input
                                            value={axeToAdd}
                                            onChange={e => setAxeToAdd(e.target.value)}
                                            onKeyDown={e => e.key === "Enter" && addAxe()}
                                            placeholder="Ex: Leadership, Management..."
                                            style={{ flex: 1 }}
                                        />
                                        <button type="button" className="cr-btn cr-btn--primary" onClick={addAxe}>+ Ajouter</button>
                                    </div>
                                </div>
                            </div>
                            <div className="cr-axes-list">
                                {axesArray.length === 0
                                    ? <div className="cr-hint">Aucun axe ajouté.</div>
                                    : axesArray.map((axe, idx) => (
                                        <div key={idx} className="cr-axe-row">
                                            <span className="cr-badge cr-badge--blue">{axe}</span>
                                            <button type="button" className="cr-icon-btn cr-icon-btn--danger" onClick={() => setAxesArray(p => p.filter((_, i) => i !== idx))}>
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* â”€â”€ AUTRES â”€â”€ */}
                    {activeSection === "autres" && (
                        <div className="cr-form-section">
                            <h3 className="cr-form-title">Paramètres email & statut</h3>
                            <div className="cr-form-grid">
                                <div className="cr-field">
                                    <label>Envoi emails</label>
                                    <PillRadioGroup
                                        name="envoiEmails"
                                        options={[
                                            { value: "INSTANTANE", label: "Instantané" },
                                            { value: "DIFFERE", label: "Différé" },
                                            { value: "SANS_EMAIL", label: "Sans email" },
                                        ]}
                                        value={form.envoiEmails}
                                        onChange={(val) => setForm(p => ({ ...p, envoiEmails: val }))}
                                    />
                                </div>
                                <div className="cr-field">
                                    <label>Statut</label>
                                    <PillRadioGroup
                                        name="status"
                                        options={[
                                            { value: "true", label: "Actif" },
                                            { value: "false", label: "Inactif" },
                                        ]}
                                        value={String(form.status)}
                                        onChange={(val) => setForm(p => ({ ...p, status: val === "true" }))}
                                    />
                                </div>
                                {form.envoiEmails === "DIFFERE" && (
                                    <div className="cr-field">
                                        <label>Date envoi emails *</label>
                                        <input type="date" value={form.dateEnvoiEmails} onChange={sf("dateEnvoiEmails")} className="cr-input-date" />
                                        {errors.dateEnvoiEmails && <div className="cr-error">{errors.dateEnvoiEmails}</div>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="cr-modal__footer">
                    <button className="cr-btn cr-btn--ghost" type="button" onClick={onClose} disabled={isLoading}>Annuler</button>
                    <button className="cr-btn cr-btn--primary" type="button" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? "Enregistrement..." : campagneId ? "Enregistrer" : "Créer la campagne"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* â”€â”€â”€ Main â”€â”€â”€ */
export default function CampagneManagement() {
    const dispatch = useDispatch();

    const campagnesState = useSelector(s => s.campagnes || {});
    const campagnes = campagnesState.list || [];
    const campagnesLoading = !!campagnesState.loading;

    const catalogues = useSelector(s => s.catalogues?.catalogues || []);
    const jobs = useSelector(s => s.jobs?.jobs || []);
    const entites = useSelector(s => s.entite?.entites || []);
    const users = useSelector(s => s.user?.users || s.users?.users || []);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selectedCampagne, setSelectedCampagne] = useState(null);
    const [tableQ, setTableQ] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchCataloguesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const openAdd = () => { setEditId(null); setFormModalOpen(true); };
    const openEdit = (c) => { setEditId(c.id); setFormModalOpen(true); };
    const openView = (c) => { setSelectedCampagne(c); setViewModalOpen(true); };

    const handleSave = useCallback((cid, payload) => {
        if (cid) dispatch(updateCampagneRequest(cid, payload));
        else dispatch(createCampagneRequest(payload));
        Swal.fire({ icon: "success", text: cid ? "Campagne modifiée" : "Campagne créée", timer: 1200, showConfirmButton: false });
        setFormModalOpen(false);
    }, [dispatch]);

    const remove = async (c) => {
        const r = await Swal.fire({
            icon: "warning", title: "Supprimer ?", text: c.nom,
            showCancelButton: true, confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385", confirmButtonText: "Supprimer", cancelButtonText: "Annuler"
        });
        if (r.isConfirmed) dispatch(deleteCampagneRequest(c.id));
    };

    const filteredCampagnes = useMemo(() => {
        const q = tableQ.trim().toLowerCase();
        const byType = (campagnes || []).filter(c => String(c.typeCampagne || "FORMATION").toUpperCase() === "FORMATION");
        const byStatus = filterStatus === "all" ? byType :
            filterStatus === "active" ? byType.filter(c => !!c.status) :
                byType.filter(c => !c.status);
        if (!q) return byStatus;
        return byStatus.filter(c => `${c.nom || ""} ${c.reference || ""} ${c.anneeCampagne || ""}`.toLowerCase().includes(q));
    }, [campagnes, tableQ, filterStatus]);

    const getCampaignDateRange = (campagne) => {
        const start = formatDateFR(campagne?.dateDebut);
        const end = formatDateFR(campagne?.dateFin);
        return `${start} - ${end}`;
    };

    return (
        <div className="campaign-management-page">
            {/* â”€â”€ Header â”€â”€ */}
            <div className="campaign-management-header">
                <div className="campaign-management-header-info">
                    <h1>Gestion des campagnes</h1>
                </div>
                <div className="campaign-management-page-actions">
                    <button className="campaign-management-btn campaign-management-btn-primary" type="button" onClick={openAdd}>
                        <Plus size={15} /> Ajouter une campagne
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="campaign-management-filter-card">
                <div className="campaign-management-tabs">
                    {[
                        { key: "all", label: "Toutes" },
                        { key: "active", label: "Actives" },
                        { key: "inactive", label: "Inactives" },
                    ].map(f => (
                        <button key={f.key} type="button"
                                className={`campaign-management-tab${filterStatus === f.key ? " is-active" : ""}`}
                                onClick={() => setFilterStatus(f.key)}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="campaign-management-search-wrap">
                    <Search size={14} className="campaign-management-search-icon" />
                    <input
                        type="text"
                        className="campaign-management-search"
                        value={tableQ}
                        onChange={e => setTableQ(e.target.value)}
                        placeholder="Rechercher une campagne..."
                    />
                </div>
                <div className="campaign-management-meta">
                    {campagnesLoading ? "..." : `${filteredCampagnes.length} campagne${filteredCampagnes.length !== 1 ? "s" : ""}`}
                </div>
            </div>

            <div className="campaign-management-table-card">
                <div className="campaign-management-table-wrap">
                    <table className="campaign-management-table">
                        <thead>
                            <tr>
                                <th>Campagne</th>
                                <th>Statut</th>
                                <th>Type</th>
                                <th>Période</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campagnesLoading ? (
                                <tr>
                                    <td colSpan={5} className="campaign-management-empty-cell">Chargement...</td>
                                </tr>
                            ) : filteredCampagnes.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="campaign-management-empty-cell">Aucune campagne de formation trouvée</td>
                                </tr>
                            ) : filteredCampagnes.map(c => (
                                <tr key={c.id}>
                                    <td>
                                        <div className="campaign-management-title-cell">
                                            <div className="campaign-management-title">{c.nom}</div>
                                            <div className="campaign-management-subtitle">
                                                {c.reference || c.description || "Aucune référence"}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`campaign-management-status-badge ${c.status ? "is-active" : "is-inactive"}`}>
                                            {c.status ? "Active" : "Clôturée"}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="campaign-management-badge">{c.typeCampagne || "FORMATION"}</span>
                                    </td>
                                    <td>
                                        <div className="campaign-management-period-cell">
                                            <span>{getCampaignDateRange(c)}</span>
                                            {c.validation ? <span className="campaign-management-inline-meta">{c.validation}</span> : null}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="campaign-management-row-actions">
                                            <button className="campaign-management-icon-btn" type="button" title="Voir les détails" onClick={() => openView(c)}>
                                                <Eye size={15} />
                                            </button>
                                            <button className="campaign-management-icon-btn" type="button" title="Modifier" onClick={() => openEdit(c)}>
                                                <Edit size={15} />
                                            </button>
                                            <button className="campaign-management-icon-btn campaign-management-icon-btn--danger" type="button" title="Supprimer" onClick={() => remove(c)}>
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <ViewModal
                open={viewModalOpen}
                campagne={selectedCampagne}
                onClose={() => { setViewModalOpen(false); setSelectedCampagne(null); }}
                catalogues={catalogues}
                entites={entites}
            />
            <FormModal
                open={formModalOpen}
                campagneId={editId}
                campagnes={campagnes}
                onClose={() => setFormModalOpen(false)}
                onSave={handleSave}
                isLoading={campagnesLoading}
                catalogues={catalogues}
                entites={entites}
                jobs={jobs}
                users={users}
            />
        </div>
    );
}
